import { FC, ReactNode, useEffect, useState } from 'react';
import { useFlag } from '@reflag/react-sdk';
import { Button, styled } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { TabType } from './types';
import Header from './Header';
import InvestorDetails from './InvestorDetails';
import InvestorJourney from './InvestorJourney';
import Communications from './Communications';
import CommitmentDetails from './CommitmentDetails';
import Notes from './Notes';
import { Vehicle } from 'further-types/lp/vehicle';
import useViewInvestor from './useViewInvestor';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import ErrorBoundary from 'components/ErrorBoundary';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import Banner from 'components/new/Banner';
import {
  createCommitmentViewRoute,
  createViewVehicleApplicationRoute,
} from 'adminConstants/routes';
import PageLoader from 'components/PageLoader';

const Container = styled(Column)({
  width: '100%',
});

const TabPanel = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'hidden',
})<{ hidden: boolean }>(({ hidden }) => ({
  display: hidden ? 'none' : 'flex',
  flex: 1,
}));

const KeepAlivePanel: FC<{ active: boolean; children: ReactNode }> = ({
  active,
  children,
}) => {
  const [mounted, setMounted] = useState(active);

  useEffect(() => {
    if (active) {
      setMounted(true);
    }
  }, [active]);

  if (!mounted) {
    return null;
  }

  return <TabPanel hidden={!active}>{children}</TabPanel>;
};

type Props = {
  vehicleApplicationId: string;
  vehicle: Vehicle;
  onClose: () => void;
  currentTab?: TabType | null;
};

const ViewInvestor: React.FC<Props> = ({
  vehicle,
  vehicleApplicationId,
  onClose,
}) => {
  const deleteCommitmentDisclosure = useDisclosure();
  const { isEnabled: showCommitmentPage } = useFlag('show-commitment-page');

  const { vehicleApplication, investor, users, isPending } =
    useVehicleApplication(vehicle._id, vehicleApplicationId);

  const {
    visibleTabs,
    selectedTab,
    handleGoToNextTab,
    handleGoToPreviousTab,
    handleChangeTab,
    isLastTab,
    isFirstTab,
    showCommitmentSettingsButtons,
    showCompleteCommitmentButton,
    showCompleteCommitmentForm,
    hideCompleteCommitmentForm,
    handleDeleteCommitment,
    completeCommitmentFormVisible,
    showSystemEmailsBanner,
  } = useViewInvestor({
    vehicleApplicationId,
    vehicleId: vehicle._id,
    onClose,
  });

  const commitmentId = vehicleApplication.commitment?.id;
  const isNotCommunicationsTab = selectedTab !== TabType.Communications;

  return (
    <FullPageDrawer
      open={true}
      size="lg"
      onClose={onClose}
      title={
        <Header
          currentTab={selectedTab}
          onTabChange={handleChangeTab}
          investorName={investor.fullName}
          tabs={visibleTabs}
          onClose={onClose}
          banner={
            <>
              {commitmentId ? (
                <Banner
                  message={`Commitment completed for this investor.${
                    showCommitmentPage ? ' Click here to view or edit' : ''
                  }`}
                  to={
                    showCommitmentPage
                      ? createCommitmentViewRoute(investor.id, commitmentId)
                      : undefined
                  }
                  openInNewTab
                  color="success"
                />
              ) : null}
              {isNotCommunicationsTab && showSystemEmailsBanner ? (
                <Banner
                  message="You have system emails available for sending"
                  to={createViewVehicleApplicationRoute(
                    vehicleApplicationId,
                    TabType.Communications,
                  )}
                  requiresAction
                />
              ) : null}
            </>
          }
        />
      }
      innerScroll
      actions={
        <Row fullWidth spacing="sm" justify="space-between">
          <Row spacing="sm">
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={onClose}
            >
              Exit
            </Button>
            {!isFirstTab && (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleGoToPreviousTab}
              >
                Previous step
              </Button>
            )}
          </Row>
          <Row spacing="sm">
            {showCompleteCommitmentButton ? (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={showCompleteCommitmentForm}
              >
                Mark subscription complete
              </Button>
            ) : null}
            {showCommitmentSettingsButtons ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={deleteCommitmentDisclosure.onOpen}
                  disabled={isPending}
                >
                  Delete commitment
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  form="commitment-settings-form"
                  disabled={isPending}
                >
                  Save and next
                </Button>
              </>
            ) : !isLastTab ? (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleGoToNextTab}
                disabled={isPending}
              >
                Next
              </Button>
            ) : null}
          </Row>
        </Row>
      }
    >
      <Container>
        <ErrorBoundary loader={<PageLoader />}>
          <KeepAlivePanel active={selectedTab === TabType.CommitmentDetails}>
            <CommitmentDetails
              application={vehicleApplication}
              vehicle={vehicle}
              onCommitmentComplete={handleGoToNextTab}
            />
          </KeepAlivePanel>
          <KeepAlivePanel active={selectedTab === TabType.InvestorDetails}>
            <InvestorDetails
              vehicleApplicationId={vehicleApplicationId}
              vehicleId={vehicle._id}
            />
          </KeepAlivePanel>
          <KeepAlivePanel active={selectedTab === TabType.InvestorJourney}>
            <InvestorJourney
              vehicle={vehicle}
              vehicleApplicationId={vehicleApplicationId}
              completeCommitmentFormVisible={completeCommitmentFormVisible}
              onHideCompleteCommitmentForm={hideCompleteCommitmentForm}
            />
          </KeepAlivePanel>
          <KeepAlivePanel active={selectedTab === TabType.Communications}>
            <Communications
              vehicleApplicationId={vehicleApplicationId}
              vehicleId={vehicle._id}
              application={vehicleApplication}
              users={users}
              showSystemEmailsBanner={showSystemEmailsBanner}
            />
          </KeepAlivePanel>
          <KeepAlivePanel active={selectedTab === TabType.Notes}>
            <Notes
              vehicleId={vehicle._id}
              vehicleApplicationId={vehicleApplicationId}
            />
          </KeepAlivePanel>
        </ErrorBoundary>
        <ConfirmDialog
          open={deleteCommitmentDisclosure.isOpen}
          onClose={deleteCommitmentDisclosure.onClose}
          title="Delete commitment"
          content="If you delete this commitment, this modal will change to show all onboarding flow steps. You can add a completed commitment again from the main Onboarding table."
          actions={{
            confirm: {
              label: 'Delete commitment',
              color: 'error',
              onClick: () => {
                deleteCommitmentDisclosure.onClose();
                handleDeleteCommitment();
              },
            },
          }}
        />
      </Container>
    </FullPageDrawer>
  );
};

export default ViewInvestor;
